import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ClickAwayListener, ListItem, Typography } from "@mui/material";
import { BrandProject } from "./constants";
import StatusIcon from "./StatusIcon";
import {
    getDownloadIconStatus,
    getPhaseColor,
    getVideoCounts,
    formatVideoCountSummary,
    BRAND_PROJECT_STATUSES,
} from "../helper";
import { IconDownload } from "../../../utils/icons";
import { useProjectDownload } from "../../../hooks/useProjectDownload";
import { DownloadOverlay } from "../../../components/DownloadOverlay";
import { DownloadVideoModal } from "../../../components/MenuOptions";
import {
    DownloadType,
    BtnText,
    getBrandAllowedFileTypes,
    isDownloadTypeVisible,
} from "../../admin/videos/selectedVideoCardV2/helpers";
import { isInternal, isBrand } from "../../../utils";

interface Props {
    project: BrandProject;
    role: string;
    user?: any;
}

const DownloadStatusIcon: React.FC<Props> = ({ project, role, user }) => {
    const downloadStatus = getDownloadIconStatus(project, role, user);
    const isBrandUser = isBrand(role);
    const projectFinished = project.status === BRAND_PROJECT_STATUSES.FINAL_DELIVERED;
    const canDownload =
        isInternal(role) ||
        !!project.brands?.some((b) => b.pre_approval_downloading) ||
        (isBrandUser && projectFinished);

    const allowedFileTypes = React.useMemo(
        () =>
            isInternal(role)
                ? null
                : getBrandAllowedFileTypes(project.brands as any),
        [role, project.brands],
    );

    const {
        showOptions,
        setShowOptions,
        isDownloading,
        downloadLink,
        setDownloadLink,
        modalOpen,
        setModalOpen,
        handleDownload,
    } = useProjectDownload({
        projectId: project.id,
        projectTitle: project.title || "project",
        allowedFileTypes,
    });

    const buttonRef = useRef<HTMLSpanElement>(null);
    const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);

    useEffect(() => {
        if (showOptions && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setMenuPosition({ top: rect.bottom + 5, right: window.innerWidth - rect.right });
        } else {
            setMenuPosition(null);
        }
    }, [showOptions]);

    const handleToggle = () => {
        if (!canDownload || downloadStatus.phase === "inactive") return;
        setShowOptions((v) => !v);
    };

    const videoCounts = getVideoCounts(project);
    const baseTooltip =
        videoCounts.total > 0 && downloadStatus.phase !== "inactive"
            ? `${downloadStatus.tooltip} ( ${formatVideoCountSummary(videoCounts)} )`
            : downloadStatus.tooltip;
    const tooltip = isDownloading ? "Downloading..." : baseTooltip;

    return (
        <>
            <DownloadOverlay visible={isDownloading} />
            <ClickAwayListener onClickAway={() => setShowOptions(false)}>
                <span ref={buttonRef} onClick={(e) => e.stopPropagation()}>
                    <StatusIcon
                        phase={downloadStatus.phase}
                        needsAction={downloadStatus.needsAction}
                        verifiedBadge={downloadStatus.verifiedBadge}
                        tooltip={tooltip}
                        count={downloadStatus.phase !== "inactive" ? videoCounts.approved : undefined}
                        icon={<IconDownload width={20} height={20} stroke={getPhaseColor(downloadStatus.phase)} />}
                        ariaLabel="Project downloads"
                        onClick={handleToggle}
                    />
                    {showOptions && menuPosition &&
                        createPortal(
                            <div
                                style={{
                                    position: "fixed",
                                    top: menuPosition.top,
                                    right: menuPosition.right,
                                    zIndex: 10000,
                                    backgroundColor: "var(--surface)",
                                    width: 300,
                                    padding: 8,
                                    border: "1px solid var(--border)",
                                    borderRadius: 8,
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {Object.values(DownloadType)
                                    .filter((key) =>
                                        isDownloadTypeVisible(key, allowedFileTypes),
                                    )
                                    .map((key) => (
                                    <ListItem
                                        key={key}
                                        className="flex !p-2 !py-3 !items-center gap-2 text-muted hover:bg-surface-2 hover:rounded-lg"
                                        onClick={() => !isDownloading && handleDownload(key)}
                                        sx={{
                                            cursor: isDownloading ? "not-allowed" : "pointer",
                                            opacity: isDownloading ? 0.5 : 1,
                                        }}
                                    >
                                        <IconDownload width={24} height={24} stroke="currentColor" />
                                        <div className="flex flex-col gap-1">
                                            <Typography variant="body2" className="text-text-2">
                                                {BtnText[key].title}
                                            </Typography>
                                            {BtnText[key].text && (
                                                <Typography variant="body2" fontSize={11} className="text-text-2">
                                                    {BtnText[key].text}
                                                </Typography>
                                            )}
                                        </div>
                                    </ListItem>
                                ))}
                            </div>,
                            document.body
                        )}
                </span>
            </ClickAwayListener>
            <DownloadVideoModal
                open={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                    setDownloadLink("");
                }}
                downloadSource={downloadLink}
            />
        </>
    );
};

export default DownloadStatusIcon;
